/**
 * Navigation menu bar component
 */

import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Dropdown from './Dropdown';
import './MenuBar.css';
import logo from '../assets/logo.svg';

const HEALTH_CHECK_INTERVAL = 30000; // 30 seconds

export default function MenuBar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const healthCheckIntervalRef = useRef<number | null>(null);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const checkHealth = async () => {
        try {
            const response = await fetch('/api/health');

            // If backend is down or returns error
            if (!response.ok) {
                console.error('Health check failed: backend returned error');
                await logout();
                navigate('/login');
                return;
            }

            const data = await response.json();

            // If backend is up but session is invalid
            if (!data.authenticated && user) {
                console.log(
                    'Session invalid, clearing and redirecting to login'
                );
                await logout();
                navigate('/login');
            }
        } catch (error) {
            // Network error or backend down
            console.error('Health check failed: backend unavailable', error);
            await logout();
            navigate('/login');
        }
    };

    useEffect(() => {
        // Only run health checks if user is logged in
        if (user) {
            // Run initial health check
            checkHealth();

            // Set up periodic health checks
            healthCheckIntervalRef.current = window.setInterval(
                checkHealth,
                HEALTH_CHECK_INTERVAL
            );
        }

        // Cleanup interval on unmount or when user changes
        return () => {
            if (healthCheckIntervalRef.current) {
                clearInterval(healthCheckIntervalRef.current);
                healthCheckIntervalRef.current = null;
            }
        };
    }, [user]); // Re-run when user changes

    return (
        <nav className="menubar-nav">
            <div className="menubar-container">
                <div className="menubar-app-title">
                    <img
                        src={logo}
                        alt="Trivia Logo"
                        className="menubar-logo"
                    />
                    <Link to="/">Trivia</Link>
                </div>

                {user && (
                    <>
                        {/* Mobile dropdown menu */}
                        <div className="menubar-mobile-nav">
                            <Dropdown label="Quizzes">
                                <Link to="/">My Quizzes</Link>
                                <Link to="/public-directory">
                                    Public Community Quizzes
                                </Link>
                                <Link to="/quiz/create">Create Quiz</Link>
                            </Dropdown>
                        </div>

                        {/* Desktop horizontal menu */}
                        <div className="menubar-desktop-nav">
                            <Link to="/" className="menubar-nav-link">
                                My Quizzes
                            </Link>
                            <Link
                                to="/public-directory"
                                className="menubar-nav-link"
                            >
                                Public Community Quizzes
                            </Link>
                            <Link
                                to="/quiz/create"
                                className="menubar-nav-link"
                            >
                                Create Quiz
                            </Link>
                        </div>
                    </>
                )}

                {user ? (
                    <div className="menubar-user-info">
                        <div>
                            Hello{' '}
                            <Link
                                to={`/user/${user.id}`}
                                className="menubar-username-link"
                            >
                                {user.username}
                            </Link>
                        </div>
                        <button
                            type="button"
                            className="menubar-logout-button"
                            onClick={handleLogout}
                        >
                            Logout
                        </button>
                    </div>
                ) : (
                    <div>&nbsp;</div>
                )}
            </div>
        </nav>
    );
}
